<template>
  <div v-if="loading" class="text-center mt-5 font-2xl text-muted">
    <i class="fal fa-spinner fa-spin" /> Customer Data Loading...
  </div>
  <div v-else class="animated fadeIn mb-5">
    <permission-alert-gate :permissions="'read customer'">
      <template #gated-content>
        <basic-heading :title="customer.full_name">
          <template #right-content>
            <header-button-group :button_config="button_config">
              <template #post_buttons>
                <button
                  v-show="checkPermission('delete customer')"
                  title="Delete customer"
                  class="btn btn-danger ml-1"
                  @click="showDeleteModal"
                >
                  <i class="fal fa-trash" />
                </button>
              </template>
            </header-button-group>
          </template>
        </basic-heading>
        <b-row class="mt-4 mb-3">
          <b-col class="pr-0">
            <general-tile
              faIcon="calendar"
              variant="primary"
              top-text="Customer Since"
              :bottom-text="submitted_date"
            />
          </b-col>
          <b-col class="pr-0">
            <general-tile
              :faIcon="project_icon"
              variant="primary"
              top-text="Projects"
              :bottom-text="project_count ? project_count : 'TBD'"
            />
          </b-col>
          <b-col class="pr-0">
            <general-tile
              faIcon="sack-dollar"
              variant="primary"
              top-text="Revenue"
              bottom-text="TBD"
            />
          </b-col>
          <b-col>
            <general-tile
              faIcon="hourglass"
              variant="primary"
              top-text="Last Order"
              :bottom-text="project_date ? project_date : 'TBD'"
            />
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <customer-card :customer="customer" />
          </b-col>
          <b-col v-if="links && links.length">
            <link-tile
              v-for="(link, linkIndex) in links"
              :key="linkIndex"
              icon-cols="2"
              :to="link.to"
              :fa-icon="link.faIcon"
              :variant="link.variant"
              :text="link.text"
            />
          </b-col>
        </b-row>
        <h5 class="text-uppercase mb-4 clearfix">
          <span>{{ "Contacts & Locations" }}</span>
          <div class="float-right">
            <b-button
              v-show="checkPermission('add customer contact')"
              variant="success"
              :to="`/customer/${customer.id}/contacts/add`"
            >
              <i class="fal fa-plus" /> Contact
            </b-button>
            <b-button
              v-show="checkPermission('add customer contact')"
              variant="success ml-1"
              :to="`/customer/${customer.id}/locations/add`"
            >
              <i class="fal fa-plus" /> Location
            </b-button>
          </div>
        </h5>
        <permission-alert-gate :permissions="'read customer contact'">
          <template #gated-content>
            <b-row>
              <b-col
                cols="6"
                v-for="customer_contact in customer_contact_collection.models"
                :key="customer_contact.id"
              >
                <customer-contact-card :customer_contact="customer_contact" />
              </b-col>
            </b-row>
            <b-row>
              <b-col
                cols="6"
                v-for="customer_location in customer_location_collection.models"
                :key="customer_location.id"
              >
                <customer-location-card
                  :customer_location="customer_location"
                />
              </b-col>
            </b-row>
          </template>
        </permission-alert-gate>
        <customer-detail-cardstack :customer="customer" />
      </template>
    </permission-alert-gate>
  </div>
</template>

<script>
import moment from "moment";
import Vue from "vue";
import VueNotifications from "vue-notifications";
import BasicHeading from "@/components/shared/BasicHeading";
import CustomerCard from "@/components/crm/customer/CustomerCard";
import CustomerContactCard from "@/components/crm/customercontact/CustomerContactCard";
import CustomerDetailCardstack from "@/components/crm/customer/CustomerDetailCardstack";
import CustomerLocationCard from "@/components/crm/customerlocation/CustomerLocationCard";
import GeneralTile from "@/components/shared/GeneralTile";
import HeaderButtonGroup from "@/components/shared/headerbuttons/HeaderButtonGroup";
import LinkTile from "@/components/shared/LinkTile";
import ProjectModel from "@/models/lab/ProjectModel";
import PermissionAlertGate from "@/components/permission/PermissionAlertGate";
import ToastNotifications from "@/components/shared/mixins/ToastNotifications";
import { AccessControlCheck } from "@/auth/AccessControlCheck";
import { CrmRepositoryFactory } from "@/repositories/crm/CrmRepositoryFactory";
import { customerNotifications } from "@/shared/notifications";
import { notificationOptions } from "@/shared/toast-notification-options";
import { getModel, getCollection } from "@/shared/fetch-utils";

Vue.use(VueNotifications, notificationOptions);

const CustomerRepository = CrmRepositoryFactory.get("customers");

export default {
  name: "CustomerDetail",
  mixins: [ToastNotifications],
  components: {
    CustomerLocationCard,
    CustomerContactCard,
    LinkTile,
    GeneralTile,
    BasicHeading,
    HeaderButtonGroup,
    PermissionAlertGate,
    CustomerDetailCardstack,
    CustomerCard,
  },
  data() {
    return {
      loading: true,
      customer_id: this.$route.params.customer_id || null,
      customer: null,
      customer_contact_collection: null,
      customer_location_collection: null,
      project_collection: null,
      project_icon: new ProjectModel().icon(),
      project_count: null,
      project_date: null,
      links: [],
    };
  },
  computed: {
    button_config() {
      return {
        buttons: [
          {
            icon: "fa-search",
            linkto: `/customer/${this.customer_id}/audit-trail`,
            title: "Audit Trail",
            variant: "dark",
          },
          {
            icon: "fa-file-chart-line",
            linkto: `/customer/${this.customer_id}/time-series-report`,
            title: "Time Series Report",
          },
          {
            icon: "fa-list",
            linkto: "/customer",
            title: "Customer list",
          },
          {
            icon: "fa-pencil",
            linkto: `/customer/${this.customer_id}/edit`,
            title: "Edit Customer",
            variant: "warning",
            permission: this.checkPermission("edit customer"),
          },
        ],
      };
    },
    submitted_date() {
      return this.customer && this.customer.created_at
        ? moment(this.customer.created_at).format("L")
        : "TBD";
    },
  },
  methods: {
    checkPermission(permission) {
      return AccessControlCheck.checkPermission(permission);
    },
    async initFromCustomer() {
      const filters = [
        {
          logic: "filterEquals",
          field: "customer_id",
          value: this.customer_id,
        },
      ];

      this.customer_contact_collection = await getCollection({
        collection_type: "customer_contact",
        filters,
        message: this.$bvToast,
      });
      this.customer_location_collection = await getCollection({
        collection_type: "customer_location",
        filters,
        message: this.$bvToast,
      });
      this.project_collection = await getCollection({
        collection_type: "project",
        filters,
        message: this.$bvToast,
      });

      this.project_count = this.project_collection.models.length;
      this.project_count &&
        (this.project_date = moment(
          this.project_collection.first().created_at
        ).format("L"));

      this.loading = false;
    },
    showDeleteModal() {
      this.$bvModal
        .msgBoxConfirm(
          "You are about to remove this customer. Do you want to continue?",
          {
            title: "Are you sure?",
            modalClass: ["modal-danger"],
            size: "sm",
            okVariant: "danger",
            okTitle: "YES",
            cancelTitle: "NO",
            footerClass: "p-2",
            centered: true,
          }
        )
        .then(async (confirm) => {
          if (confirm) {
            try {
              await CustomerRepository.suspend(this.customer_id);
  
              this.deleteSuccess();
              this.$router.push("/customer");
            } catch ({ response }) {
              const { status, data } = response.response;
  
              this.$bvToast.toast(`Code: ${status}. ${data.error}`, {
                title: "Server Error.",
                variant: "danger",
              });
            }            
          }
        });
    },
  },
  async created() {
    this.loading = true;

    this.customer = await getModel({
      model_type: "customer",
      id: this.customer_id,
      message: this.$bvToast,
    });

    if (this.customer) {
      this.initFromCustomer();
    } else {
      this.$router.back();
    }
  },
  notifications: customerNotifications,
};
</script>
